/**
 * Hero phone screen — angled scrolling view.
 *
 * Maps the rectangular .phone-screen overlay onto the four (perspective-skewed)
 * corners of the phone screen in the hero image, then auto-scrolls the inner
 * "Lediga uppdrag" feed downwards in an infinite seamless loop. The corner
 * positions are stored as fractions of the
 * phone image's rendered box in CSS custom properties, so the transform stays
 * pixel-perfect at any viewport size.
 */

/**
 * Solve the 3x3 homography H such that H * [x,y,1]^T = [x',y',w]^T (normalized
 * by w), mapping the four `src` points onto the four `dst` points.
 */
function solveHomography(src, dst) {
    const A = [];
    const b = [];
    for (let i = 0; i < 4; i++) {
        const [x, y] = src[i];
        const [xp, yp] = dst[i];
        A.push([x, y, 1, 0, 0, 0, -x * xp, -y * xp]);
        A.push([0, 0, 0, x, y, 1, -x * yp, -y * yp]);
        b.push(xp);
        b.push(yp);
    }

    // Solve A * h = b for the 8 unknowns via Gaussian elimination.
    const n = 8;
    for (let i = 0; i < n; i++) {
        let maxRow = i;
        for (let k = i + 1; k < n; k++) {
            if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) maxRow = k;
        }
        [A[i], A[maxRow]] = [A[maxRow], A[i]];
        [b[i], b[maxRow]] = [b[maxRow], b[i]];

        for (let k = i + 1; k < n; k++) {
            const factor = A[k][i] / A[i][i];
            for (let j = i; j < n; j++) A[k][j] -= factor * A[i][j];
            b[k] -= factor * b[i];
        }
    }
    const h = new Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
        let sum = b[i];
        for (let j = i + 1; j < n; j++) sum -= A[i][j] * h[j];
        h[i] = sum / A[i][i];
    }
    return [
        [h[0], h[1], h[2]],
        [h[3], h[4], h[5]],
        [h[6], h[7], 1],
    ];
}

function applyTransform(frame, screen) {
    const rect = frame.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;
    if (!W || !H) return;

    const cs = getComputedStyle(screen);
    const baseW = parseFloat(cs.getPropertyValue('--screen-base-w')) || 400;
    const baseH = parseFloat(cs.getPropertyValue('--screen-base-h')) || 900;

    // Corner positions as fractions of the phone image box.
    const fracs = ['tl-x', 'tl-y', 'tr-x', 'tr-y', 'bl-x', 'bl-y', 'br-x', 'br-y']
        .map((v) => parseFloat(cs.getPropertyValue('--' + v)));
    if (fracs.some((n) => Number.isNaN(n))) return;

    // Source: the overlay's four corners in its own (pre-transform) coords.
    const src = [
        [0, 0],
        [baseW, 0],
        [0, baseH],
        [baseW, baseH],
    ];
    // Destination: the screen corners, in image pixel coords.
    const dst = [
        [fracs[0] * W, fracs[1] * H], // TL
        [fracs[2] * W, fracs[3] * H], // TR
        [fracs[4] * W, fracs[5] * H], // BL
        [fracs[6] * W, fracs[7] * H], // BR
    ];

    const Hm = solveHomography(src, dst);

    // Embed the 3x3 homography into a column-major CSS matrix3d.
    const [[a, b, c], [d, e, f], [g, h]] = Hm;
    const m3d = [a, d, 0, g, b, e, 0, h, 0, 0, 1, 0, c, f, 0, 1]
        .map((v) => v.toFixed(8))
        .join(',');

    screen.style.transform = `matrix3d(${m3d})`;
}

/**
 * Infinite downward scroll, driven by the Web Animations API. The feed is
 * duplicated once (see initHeroPhone), so translating by exactly one copy's
 * height and restarting lands on identical pixels — a seamless loop.
 *
 * Using WAAPI instead of a CSS @keyframes rule means the distance and duration
 * can be updated in place (fonts loading, resize) without redefining running
 * keyframes, which would make the feed jump.
 */
const SCROLL_SPEED = 100; // px per second

const reducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function calibrateScroll(content, state) {
    if (reducedMotion) return;

    const period = content.scrollHeight / 2;
    if (!period || period === state.period) return;
    state.period = period;

    const keyframes = [
        { transform: 'translateY(0)' },
        { transform: `translateY(-${period}px)` },
    ];
    const duration = (period / SCROLL_SPEED) * 1000;

    if (state.animation) {
        state.animation.effect.setKeyframes(keyframes);
        state.animation.effect.updateTiming({ duration });
        return;
    }

    state.animation = content.animate(keyframes, {
        duration,
        iterations: Infinity,
        easing: 'linear',
    });
}

/** Resolve once the image has loaded (or immediately if it already has / failed). */
function whenImageLoaded(img) {
    if (!img || img.complete) return Promise.resolve();
    return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
    });
}

export function initHeroPhone() {
    const frame = document.querySelector('.hero-phone');
    if (!frame) return;
    const screen = frame.querySelector('.phone-screen');
    const content = frame.querySelector('.screen-content');
    if (!screen || !content) return;

    // Append a second copy of the feed so the scroll can loop seamlessly.
    // Done before the overlay is revealed, so the duplication never paints.
    content.innerHTML += content.innerHTML;

    const state = { period: 0, animation: null };

    const update = () => {
        applyTransform(frame, screen);
        calibrateScroll(content, state);
    };

    // Position and calibrate once the phone image has its final box and the
    // webfonts are in, then reveal. Doing everything in one go avoids the
    // visible snap of the overlay being repositioned / the scroll retargeted.
    const fontsReady =
        document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

    Promise.all([whenImageLoaded(frame.querySelector('img')), fontsReady]).then(() => {
        update();
        frame.classList.add('is-ready');
    });

    window.addEventListener('resize', update);
}
